import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchTariffTypes } from '../data-provider';
import type { TariffType, TariffTypeFilters, TariffTypes } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal tariff type lookup cache used to resolve tariff type references
 * without repeatedly requesting the same tariff type data.
 */

export const tariffTypesQueryOptions = (
  filters: Partial<TariffTypeFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const tariffTypes = await fetchTariffTypes(filters, { signal });
      put(tariffTypes.list);
      return tariffTypes;
    },
    queryKey: CACHE_KEYS.tariffTypes(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_TARIFF_TYPES_CACHE_KEY = btoa('internal-tariff-types-cache-key');

const INTERNAL_QUERY_TARIFF_TYPES_KEY = tariffTypesQueryOptions({
  codeDesc: INTERNAL_TARIFF_TYPES_CACHE_KEY,
}).queryKey;

const getTariffTypeFallback = (tariffTypeId: string): TariffType => {
  logger.warn(`Tariff type "${tariffTypeId}" was not found. Verify the data model.`);
  return {
    codeDesc: tariffTypeId,
    id: tariffTypeId,
    label: tariffTypeId,
    lookupCodeType: 'BASIS',
  };
};

export const put = async (tariffTypes: TariffType[]) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_TARIFF_TYPES_KEY);

  const newTariffTypes = tariffTypes.reduce(
    (acc, tariffType) => {
      acc[String(tariffType.id)] = tariffType;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_TARIFF_TYPES_KEY, {
    list: Object.values(newTariffTypes),
    map: newTariffTypes,
  });
};

export const getAll = (): TariffTypes | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_TARIFF_TYPES_KEY);
};

export const find = async (tariffTypeId: string): Promise<TariffType> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_TARIFF_TYPES_KEY);

  const tariffType = data?.map[tariffTypeId];
  if (tariffType) {
    return tariffType;
  }

  try {
    const newData = await queryClient.query(tariffTypesQueryOptions({ search: { codeId: tariffTypeId } }));
    if (newData.map[tariffTypeId]) {
      void put(newData.list);
      return newData.map[tariffTypeId];
    }
  } catch {
    return getTariffTypeFallback(tariffTypeId);
  }

  return getTariffTypeFallback(tariffTypeId);
};

export const findAll = async (tariffTypeIds: string[]): Promise<TariffType[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_TARIFF_TYPES_KEY);

  const missingIds: string[] = [];
  const tariffTypes: TariffType[] = [];

  for (const tariffTypeId of tariffTypeIds) {
    const t = data?.map[tariffTypeId];
    if (t) {
      tariffTypes.push(t);
    } else {
      missingIds.push(tariffTypeId);
    }
  }

  if (!missingIds.length) {
    return tariffTypes;
  }

  try {
    const newData = await queryClient.query(tariffTypesQueryOptions({ search: { codeId: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const t = newData.map[missingId];
      if (t) {
        tariffTypes.push(t);
      } else {
        tariffTypes.push(getTariffTypeFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      tariffTypes.push(getTariffTypeFallback(missingId));
    }
  }

  return tariffTypes;
};
